/**
 * Real-Debrid API service
 * Converts magnet links to direct streaming URLs
 */

const RD_API_KEY = import.meta.env.VITE_REAL_DEBRID_API_KEY;
const CORS_PROXY_URL = import.meta.env.VITE_CORS_PROXY_URL;

// Use proxy for production (HTTPS), direct API for localhost
const useProxy = CORS_PROXY_URL && typeof window !== "undefined" && window.location.protocol === "https:";
const RD_API_BASE = useProxy ? `${CORS_PROXY_URL}/rd` : "https://api.real-debrid.com/rest/1.0";

/**
 * Add a magnet link to Real-Debrid and get the torrent ID
 * @param {string} magnetUrl - The magnet URL
 * @returns {Promise<{id: string, uri: string, error?: string} | null>}
 */
const addMagnet = async (magnetUrl) => {
  try {
    const response = await fetch(`${RD_API_BASE}/torrents/addMagnet`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${RD_API_KEY}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: `magnet=${encodeURIComponent(magnetUrl)}`,
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Real-Debrid addMagnet error:", data);
      return { error: data.error || `HTTP ${response.status}` };
    }

    return data;
  } catch (error) {
    console.error("Error adding magnet:", error);
    return { error: error.message || "Network error" };
  }
};

/**
 * Get torrent info from Real-Debrid
 * @param {string} torrentId - The torrent ID
 * @returns {Promise<object | null>}
 */
const getTorrentInfo = async (torrentId) => {
  try {
    const response = await fetch(`${RD_API_BASE}/torrents/info/${torrentId}`, {
      headers: {
        Authorization: `Bearer ${RD_API_KEY}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting torrent info:", error);
    return null;
  }
};

/**
 * Select files from a torrent (usually the largest video file)
 * @param {string} torrentId - The torrent ID
 * @param {string} files - Comma-separated file IDs or "all"
 */
const selectFiles = async (torrentId, files = "all") => {
  try {
    const response = await fetch(`${RD_API_BASE}/torrents/selectFiles/${torrentId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${RD_API_KEY}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: `files=${files}`,
    });

    return response.ok;
  } catch (error) {
    console.error("Error selecting files:", error);
    return false;
  }
};

/**
 * Unrestrict a link to get direct download URL
 * @param {string} link - The link to unrestrict
 * @returns {Promise<{download: string, streaming: string} | null>}
 */
const unrestrictLink = async (link) => {
  try {
    const response = await fetch(`${RD_API_BASE}/unrestrict/link`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${RD_API_KEY}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: `link=${encodeURIComponent(link)}`,
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error("Error unrestricting link:", error);
    return null;
  }
};

/**
 * Convert a magnet URL to a direct streaming URL via Real-Debrid
 * @param {string} magnetUrl - The magnet URL
 * @param {function} onProgress - Callback for progress updates
 * @returns {Promise<{streamUrl: string, filename: string} | {error: string}>}
 */
export const getStreamFromMagnet = async (magnetUrl, onProgress = () => {}) => {
  if (!RD_API_KEY) {
    return { error: "Real-Debrid no está configurado" };
  }

  try {
    // Step 1: Add magnet
    onProgress("Añadiendo torrent a Real-Debrid...");
    const magnetResult = await addMagnet(magnetUrl);

    if (magnetResult?.error) {
      return { error: `Error de Real-Debrid: ${magnetResult.error}` };
    }

    if (!magnetResult?.id) {
      return { error: "Error al añadir el magnet" };
    }

    const torrentId = magnetResult.id;

    // Step 2: Get torrent info to see files
    onProgress("Obteniendo información del torrent...");
    let info = await getTorrentInfo(torrentId);

    if (!info) {
      return { error: "Error al obtener info del torrent" };
    }

    // Step 3: Select files if not already selected
    if (info.status === "waiting_files_selection") {
      onProgress("Analizando archivos del torrent...");

      // Find video files
      const videoFiles = info.files?.filter((f) => /\.(mkv|mp4|avi|mov|webm)$/i.test(f.path)) || [];

      console.log(`Found ${videoFiles.length} video files in torrent`);

      // Movie pack detected - return file list for user selection
      if (videoFiles.length > 10) {
        console.log("Movie pack detected with", videoFiles.length, "files - returning list for selection");
        return {
          isPack: true,
          torrentId: torrentId,
          files: videoFiles.map((f) => ({
            id: f.id,
            path: f.path,
            filename: f.path.split("/").pop(),
            bytes: f.bytes,
            size: (f.bytes / 1024 / 1024 / 1024).toFixed(2) + " GB",
          })),
        };
      }

      console.log(
        "Available video files in torrent:",
        videoFiles.map((f) => ({
          id: f.id,
          path: f.path,
          size: (f.bytes / 1024 / 1024 / 1024).toFixed(2) + " GB",
        })),
      );

      if (videoFiles.length > 0) {
        // Select the largest video file
        const largestFile = videoFiles.reduce((a, b) => (a.bytes > b.bytes ? a : b));
        console.log("Selected file:", largestFile.path, "ID:", largestFile.id);
        await selectFiles(torrentId, String(largestFile.id));
      } else {
        // Select all files
        console.log("No video files found, selecting all");
        await selectFiles(torrentId, "all");
      }

      // Wait a bit and get updated info
      await new Promise((resolve) => setTimeout(resolve, 1000));
      info = await getTorrentInfo(torrentId);
    } else {
      // Already selected - log what we got
      console.log("Torrent already processed, status:", info.status);
      console.log("Links available:", info.links?.length || 0);
    }

    // Step 4: Wait for download to complete (if cached, this is instant)
    let attempts = 0;
    const maxAttempts = 60; // 60 seconds max

    while (info?.status !== "downloaded" && attempts < maxAttempts) {
      if (info?.status === "downloading") {
        onProgress(`Descargando en Real-Debrid: ${info.progress}%`);
      } else if (info?.status === "magnet_conversion") {
        onProgress("Convirtiendo magnet...");
      } else if (info?.status === "queued") {
        onProgress("En cola...");
      } else {
        onProgress(`Estado: ${info?.status || "desconocido"}`);
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      info = await getTorrentInfo(torrentId);
      attempts++;
    }

    if (info?.status !== "downloaded") {
      return { error: "Tiempo de espera agotado. Intenta más tarde." };
    }

    // Step 5: Get the streaming link
    onProgress("Obteniendo enlace de streaming...");

    if (!info.links || info.links.length === 0) {
      return { error: "No se encontraron enlaces de descarga" };
    }

    console.log("Available links from torrent:", info.links);
    console.log("Using first link:", info.links[0]);

    // Unrestrict the first link (usually the video)
    const unrestricted = await unrestrictLink(info.links[0]);
    console.log("Unrestricted filename:", unrestricted?.filename);

    if (!unrestricted?.download) {
      return { error: "Error al obtener el enlace de streaming" };
    }

    console.log("Real-Debrid unrestricted response:", unrestricted);
    console.log("Available streaming options:", unrestricted.streaming);

    // Check if streaming transcodes are available (for browser-compatible audio)
    let streamUrl = unrestricted.download;

    // Real-Debrid provides transcoded streams in the 'streaming' object
    // These have browser-compatible audio (AAC instead of AC3/DTS)
    if (unrestricted.streaming) {
      // Prefer liveMP4 for browser compatibility
      if (unrestricted.streaming.liveMP4) {
        console.log("Using liveMP4 stream");
        streamUrl = unrestricted.streaming.liveMP4;
      } else if (unrestricted.streaming.h264_webm) {
        console.log("Using h264_webm stream");
        streamUrl = unrestricted.streaming.h264_webm;
      } else if (unrestricted.streaming.apple) {
        console.log("Using apple stream (HLS)");
        streamUrl = unrestricted.streaming.apple;
      } else {
        console.log("No transcoded stream available, using direct download");
      }
    } else {
      console.log("No streaming object available");
    }

    console.log("Final stream URL:", streamUrl);

    return {
      streamUrl,
      downloadUrl: unrestricted.download,
      filename: unrestricted.filename || "Video",
      streaming: unrestricted.streaming,
    };
  } catch (error) {
    console.error("Real-Debrid error:", error);
    return { error: "Error de conexión con Real-Debrid" };
  }
};

/**
 * Continue streaming after user selects a file from a pack
 */
export const continueWithSelectedFile = async (torrentId, fileId, onProgress = () => {}) => {
  try {
    onProgress("Seleccionando archivo...");
    await selectFiles(torrentId, String(fileId));

    // Wait a bit and get updated info
    await new Promise((resolve) => setTimeout(resolve, 1000));
    let info = await getTorrentInfo(torrentId);

    // Wait for download if needed
    let attempts = 0;
    const maxAttempts = 60;

    while (info?.status !== "downloaded" && attempts < maxAttempts) {
      if (info?.status === "downloading") {
        onProgress(`Descargando en Real-Debrid: ${info.progress}%`);
      } else if (info?.status === "magnet_conversion") {
        onProgress("Convirtiendo magnet...");
      } else {
        onProgress(`Estado: ${info?.status || "desconocido"}`);
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      info = await getTorrentInfo(torrentId);
      attempts++;
    }

    if (info?.status !== "downloaded") {
      return { error: "Tiempo de espera agotado. Intenta más tarde." };
    }

    onProgress("Obteniendo enlace de streaming...");

    if (!info.links || info.links.length === 0) {
      return { error: "No se encontraron enlaces de descarga" };
    }

    const unrestricted = await unrestrictLink(info.links[0]);
    console.log("Unrestricted filename:", unrestricted?.filename);

    if (!unrestricted?.download) {
      return { error: "Error al obtener el enlace de streaming" };
    }

    let streamUrl = unrestricted.download;

    if (unrestricted.streaming) {
      if (unrestricted.streaming.h264_1080p) {
        streamUrl = unrestricted.streaming.h264_1080p;
      } else if (unrestricted.streaming.h264_720p) {
        streamUrl = unrestricted.streaming.h264_720p;
      } else if (unrestricted.streaming.h264_480p) {
        streamUrl = unrestricted.streaming.h264_480p;
      } else if (unrestricted.streaming.h264_webm) {
        streamUrl = unrestricted.streaming.h264_webm;
      }
    }

    return {
      streamUrl,
      downloadUrl: unrestricted.download,
      filename: unrestricted.filename || "Video",
      streaming: unrestricted.streaming,
    };
  } catch (error) {
    console.error("Continue streaming error:", error);
    return { error: "Error de conexión con Real-Debrid" };
  }
};

/**
 * Check if Real-Debrid is configured
 */
export const isRealDebridConfigured = () => !!RD_API_KEY;
